// Describe only the repeated cards intersecting the viewport (plus the caller's
// small overscan). Offscreen rounds retain their width through plain spacers.
export function visibleRoundSlices({ start, end, currentRound, originRound, leadingWidth,
  sourceWidth, roundWidth, cardStart, stride, count }) {
  if (roundWidth <= 0 || stride <= 0 || !count || end <= start) return [];
  const prefix = leadingWidth + (currentRound - originRound) * roundWidth;
  const sourceEnd = prefix + sourceWidth;
  const slices = [];
  const append = (round, left) => {
    if (left >= end || left + roundWidth <= start) return;
    const first = Math.max(0, Math.min(count, Math.floor((start - left - cardStart) / stride)));
    const last = Math.max(-1, Math.min(count - 1, Math.ceil((end - left - cardStart) / stride) - 1));
    slices.push({ round, left, first, last });
  };
  const firstPast = Math.max(originRound, originRound + Math.floor((start - leadingWidth) / roundWidth));
  const lastPast = Math.min(currentRound - 1, originRound + Math.floor((end - leadingWidth) / roundWidth));
  for (let round = firstPast; round <= lastPast; round++) {
    append(round, leadingWidth + (round - originRound) * roundWidth);
  }
  const firstFuture = Math.max(0, Math.floor((start - sourceEnd) / roundWidth));
  const lastFuture = Math.floor((end - sourceEnd) / roundWidth);
  for (let offset = firstFuture; offset <= lastFuture; offset++) {
    append(currentRound + 1 + offset, sourceEnd + offset * roundWidth);
  }
  return slices;
}

// Each character has exactly one fully visible occurrence: its active or next turn.
export function nextOccurrenceRound(index, activeIndex, currentRound) {
  return currentRound + (activeIndex >= 0 && index < activeIndex ? 1 : 0);
}

// Keep surviving nodes connected. Reattaching a visible round can reset image
// presentation, observer state and animations even when its content is unchanged.
export function syncChildren(parent, children) {
  const retained = new Set(children);
  for (const child of [...parent.children]) if (!retained.has(child)) child.remove();
  children.forEach((child, index) => {
    if (parent.children[index] !== child) parent.insertBefore(child, parent.children[index] || null);
  });
}

export function orderedCharacters(characters) {
  return [...characters]
    .filter(character => !character.classList.contains('is-hidden'))
    .sort((a, b) => Number(a.dataset.position) - Number(b.dataset.position));
}

export function presentationCard(character) {
  const slot = document.createElement('div');
  slot.className = 'character-list-2__slot is-repeat';
  slot.dataset.characterId = character.dataset.characterId;
  const card = character.querySelector('.character-list-2__card').cloneNode(true);
  for (const element of [card, ...card.querySelectorAll('*')]) {
    const iconUrl = element.getAttribute('data-condition-icon-url-value');
    const title = element.getAttribute('data-bs-original-title');
    if (title) element.setAttribute('title', title);
    for (const attribute of [...element.attributes]) {
      if (attribute.name === 'id' || attribute.name.startsWith('data-') ||
          ['aria-describedby', 'aria-labelledby', 'aria-current', 'tabindex'].includes(attribute.name)) {
        element.removeAttribute(attribute.name);
      }
    }
    // Copies may be made before the source's asynchronous SVG load finishes.
    if (iconUrl) {
      element.setAttribute('data-controller', 'condition-icon');
      element.setAttribute('data-condition-icon-url-value', iconUrl);
    }
  }
  for (const property of ['translate', 'scale', 'transition', 'z-index', 'visibility']) {
    card.style.removeProperty(property);
  }
  slot.append(card);
  return slot;
}

export function offsetInTrack(element, track) {
  let offset = 0;
  for (let current = element; current && current !== track; current = current.offsetParent) {
    offset += current.offsetLeft;
  }
  return offset;
}
